import { Component, OnInit, AfterViewInit, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ListItemRendererComponent } from 'gung-list';
import { CustomerRequest, CustomerRequestService } from '../../../services/customer-request/customer-request.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { CreateCustomerModalComponent } from '../../create-customer-modal/create-customer-modal.component';

@Component({
  selector: 'lib-customer-requests-card-list',
  templateUrl: './customer-requests-card-list.component.html',
  styleUrls: ['./customer-requests-card-list.component.scss'],
  standalone: false
})
export class CustomerRequestsCardListComponent extends ListItemRendererComponent<CustomerRequest[]> implements OnInit, AfterViewInit {
  protected customerRequestService = inject(CustomerRequestService);
  protected gungModalService = inject(GungModalService);
  protected translationService = inject(TranslateService);
  protected modalService = inject(NgbModal);

  public showCreatedCustomerData = true;

  public editingCommentId?: string;
  public commentDraft = '';
  public savingComment = false;

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.renderFinished?.emit();
  }

  public createCustomer(request: CustomerRequest) {
    const modalRef = this.modalService.open(CreateCustomerModalComponent, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.customer = {
      id: '',
      name: request.companyName,
      extra: {}
    };
    modalRef.result.then(
      result => {
        if (result?.id) {
          this.customerRequestService.markAsProcessed(request.id).subscribe();
        }
      },
      () => {}
    );
  }

  public processed(request: CustomerRequest) {
    this.gungModalService
      .openConfirmYesNoModal(
        this.translationService.instant('MARK_PROCESSED'),
        this.translationService.instant('ACCOUNT_PROCESS', { name: request.companyName }) + '?',
        { size: 'sm' }
      )
      .then(result => {
        if (result) {
          this.customerRequestService.markAsProcessed(request.id).subscribe();
        }
      });
  }

  public rejectRequest(request: CustomerRequest) {
    this.gungModalService
      .openConfirmYesNoModal(
        this.translationService.instant('REJECT'),
        this.translationService.instant('ACCOUNT_REQUEST_REJECT', { name: request.companyName }) + '?',
        { size: 'sm' }
      )
      .then(result => {
        if (result) {
          this.customerRequestService.markAsRejected(request.id).subscribe();
        }
      });
  }

  public startEditComment(request: CustomerRequest) {
    this.editingCommentId = request.id;
    this.commentDraft = request.extra?.comment || '';
  }

  public cancelEditComment() {
    this.editingCommentId = undefined;
    this.commentDraft = '';
  }

  public saveComment(request: CustomerRequest) {
    const comment = this.commentDraft.trim();
    this.savingComment = true;
    this.customerRequestService.saveComment(request.id, comment).subscribe({
      next: () => {
        // Reflect it on the in-memory request too: the list is rendered from `data`, which the
        // subject refresh only replaces once the reload comes back.
        if (!request.extra) {
          request.extra = {};
        }
        if (comment) {
          request.extra.comment = comment;
        } else {
          delete request.extra.comment;
        }
        this.savingComment = false;
        this.cancelEditComment();
      },
      error: () => {
        this.savingComment = false;
        this.gungModalService.openJSONModal({
          title: 'COMMENT_NOT_SAVED',
          json: this.translationService.instant('COMMENT_NOT_SAVED'),
          typeString: true
        } as any);
      }
    });
  }

  public reactivateRequest(request: CustomerRequest) {
    this.gungModalService
      .openConfirmYesNoModal(
        this.translationService.instant('REACTIVATE_REQUEST'),
        this.translationService.instant('ACCOUNT_REQUEST_REACTIVATE', { name: request.companyName }) + '?',
        { size: 'sm' }
      )
      .then(result => {
        if (result) {
          this.customerRequestService.markAsPending(request.id).subscribe();
        }
      });
  }
}
